import { toBusinessSummary } from "@nwga/features/businesses/domain";
import { useTRPC } from "@nwga/features/client";
import { JobForm, JobPreviewDialog } from "@nwga/features/jobs/ui";
import { Badge } from "@nwga/ui/components/badge";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, EyeIcon } from "lucide-react";
import * as React from "react";
import { ErrorState } from "../../../../components/error-state.tsx";
import { PageHeader } from "../../../../components/page-header.tsx";

/**
 * Editing a listing.
 *
 * The preview button is why this page has a header of its own: a draft has no
 * public URL to open, so without it the only way to see what you have written
 * is to publish it and look.
 */
export const Route = createFileRoute("/dashboard/$businessSlug/jobs/$jobId")({
	component: EditJobPage,
	loader: ({ context, params }) =>
		Promise.all([
			context.queryClient.ensureQueryData(
				context.trpc.businesses.bySlug.queryOptions({
					slug: params.businessSlug,
				}),
			),
			context.queryClient.ensureQueryData(
				context.trpc.jobs.byId.queryOptions({ jobId: params.jobId }),
			),
		]),
});

function EditJobPage() {
	const { businessSlug, jobId } = Route.useParams();
	const trpc = useTRPC();
	const navigate = useNavigate();
	const [previewing, setPreviewing] = React.useState(false);

	const business = useQuery(
		trpc.businesses.bySlug.queryOptions({ slug: businessSlug }),
	);
	const job = useQuery(trpc.jobs.byId.queryOptions({ jobId }));

	if (business.isError || job.isError) {
		return (
			<ErrorState
				error={business.error ?? job.error}
				onRetry={() => {
					void business.refetch();
					void job.refetch();
				}}
			/>
		);
	}

	if (!business.isSuccess || !job.isSuccess)
		return <Skeleton className="h-96 w-full" />;

	return (
		<div className="max-w-2xl space-y-6">
			<Button asChild variant="ghost" size="sm" className="-ml-2">
				<Link to="/dashboard/$businessSlug/jobs" params={{ businessSlug }}>
					<ArrowLeftIcon className="size-4" aria-hidden="true" />
					Job listings
				</Link>
			</Button>

			<PageHeader
				title="Edit listing"
				{...(job.data.status === "published"
					? {
							description:
								"This listing is live — changes appear on the board immediately.",
						}
					: {})}
				meta={
					<Badge
						variant={job.data.status === "published" ? "default" : "outline"}
						className="capitalize"
					>
						{job.data.status}
					</Badge>
				}
				actions={
					<Button
						variant="outline"
						size="sm"
						onClick={() => setPreviewing(true)}
					>
						<EyeIcon className="size-4" aria-hidden="true" />
						Preview
					</Button>
				}
			/>

			<JobForm
				businessId={business.data.id}
				job={job.data}
				canPublish={business.data.status === "active"}
				onSaved={() =>
					void navigate({
						to: "/dashboard/$businessSlug/jobs",
						params: { businessSlug },
					})
				}
			/>

			<JobPreviewDialog
				job={job.data}
				business={toBusinessSummary(business.data)}
				open={previewing}
				onOpenChange={setPreviewing}
			/>
		</div>
	);
}
